import { useEffect, useRef, useState } from 'react'
import type { Navigate } from '../App'
import { useIsland, useSettings } from '../state/store'
import { useRecorder } from '../state/useRecorder'
import type { GradeResult } from '../lib/grade'
import { gradeFor, inputClass, languageOf, resolveMode } from '../lib/languages'
import { scoreClass } from '../lib/analysis'
import { Countdown, useCountdown } from '../components/Countdown'
import { GradeReport } from '../components/GradeReport'
import { DrillExit } from '../components/DrillExit'

type Phase = 'ready' | 'live' | 'review' | 'done'

/**
 * The whole island, blind, against a clock.
 *
 * Recall lets you sit and think, which is exactly the habit that falls apart in
 * a real conversation. Here the block is gone from the screen, the timer is
 * running, and whatever you have typed when it hits zero is what you said.
 */
export function PerformDrill({
  id,
  navigate,
  setFocused,
}: {
  id: string
  navigate: Navigate
  setFocused: (v: boolean) => void
}) {
  const island = useIsland(id)
  const settings = useSettings()
  const record = useRecorder()

  const [phase, setPhase] = useState<Phase>('ready')
  const [index, setIndex] = useState(0)
  const [attempt, setAttempt] = useState('')
  const [result, setResult] = useState<GradeResult | null>(null)
  const [scores, setScores] = useState<number[]>([])
  const startedAt = useRef(Date.now())
  const attemptRef = useRef('')
  attemptRef.current = attempt

  const lang = languageOf(island?.language)
  const mode = resolveMode(lang.code, settings.inputMode)
  const limit = settings.performSecondsPerBlock
  const block = island?.blocks[index]

  const submit = () => {
    if (!block) return
    setResult(gradeFor(lang.code, block, attemptRef.current, mode, settings.requireTones))
    setPhase('review')
  }

  const remaining = useCountdown(limit, phase === 'live', submit)

  useEffect(() => {
    setFocused(phase === 'live')
  }, [phase, setFocused])

  useEffect(() => () => setFocused(false), [setFocused])

  if (!island || !block) {
    return (
      <div className="empty">
        Nothing to perform.{' '}
        <button type="button" className="btn is-small" onClick={() => navigate({ view: 'islands' })}>
          Back to islands
        </button>
      </div>
    )
  }

  const isLast = index === island.blocks.length - 1
  const average = scores.length
    ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length)
    : 0

  const advance = () => {
    if (!result) return
    const collected = [...scores, result.score]
    setScores(collected)

    if (isLast) {
      const finalScore = collected.reduce((a, b) => a + b, 0) / collected.length
      record({
        islandId: island.id,
        mode: 'perform',
        score: finalScore,
        seconds: (Date.now() - startedAt.current) / 1000,
        inputMode: mode,
        result,
      })
      setPhase('done')
      return
    }

    setIndex((i) => i + 1)
    setAttempt('')
    setResult(null)
    setPhase('live')
  }

  if (phase === 'ready') {
    return (
      <>
        <button
          type="button"
          className="btn is-quiet is-small"
          onClick={() => navigate({ view: 'island', id: island.id })}
          style={{ marginBottom: '0.8rem' }}
        >
          ← Back
        </button>

        <header className="page-head">
          <div className="eyebrow">Perform · {island.title}</div>
          <h1 className="page-title">
            <span className="page-title-zh">{lang.ui.perform}</span> Against the clock
          </h1>
          <p className="lede">
            {island.blocks.length} blocks, {limit} seconds each, nothing on screen but the English.
            When the clock runs out, whatever you have typed is what you said.
          </p>
        </header>

        <div className="card">
          <div className="row">
            <button type="button" className="btn is-live" onClick={() => {
              startedAt.current = Date.now()
              setPhase('live')
            }}>
              Start — {limit}s per block
            </button>
            <span className="muted" style={{ fontSize: '0.85rem' }}>
              Typing in {mode === 'hanzi' ? '汉字' : mode === 'pinyin' ? 'pinyin' : lang.name}.
            </span>
          </div>
        </div>
      </>
    )
  }

  if (phase === 'done') {
    return (
      <>
        <header className="page-head">
          <div className="eyebrow">Perform · {island.title}</div>
          <h1 className="page-title">
            <span className="page-title-zh">{lang.ui.result}</span> Curtain down
          </h1>
        </header>

        <div className="card">
          <div className={`score ${scoreClass(average)}`}>{average}</div>
          <div className="stat-label">island average across {scores.length} blocks</div>
          <div className="divider" />
          <div className="tally">
            {scores.map((s, i) => (
              <span key={i}>
                block {i + 1} <span className="tally-n">{s}</span>
              </span>
            ))}
          </div>
          <div className="divider" />
          <div className="row">
            <button
              type="button"
              className="btn is-primary"
              onClick={() => navigate({ view: 'island', id: island.id })}
            >
              Back to island
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => {
                setPhase('ready')
                setIndex(0)
                setScores([])
                setAttempt('')
                setResult(null)
              }}
            >
              Run it again
            </button>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <DrillExit
        onLeave={() => navigate({ view: 'island', id: island.id })}
        label="Leave"
        hint={phase === 'live' ? 'The run so far will not be logged.' : undefined}
      />

      <div className="beat-dots" aria-hidden="true">
        {island.blocks.map((b, i) => (
          <span
            key={b.id}
            className={`beat-dot ${i < index ? 'is-done' : ''} ${i === index ? 'is-current' : ''}`}
          />
        ))}
      </div>

      <div className={`stage ${phase === 'live' ? 'is-live' : ''}`}>
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div className="stage-cue">
            Block {index + 1} of {island.blocks.length} — say it in {lang.name}
          </div>
          {phase === 'live' && <Countdown remaining={remaining} total={limit} />}
        </div>

        <p style={{ fontSize: '1.45rem', margin: '0.4rem 0 0' }}>{block.english}</p>
      </div>

      <div className="card">
        <input
          className={`answer-input ${inputClass(mode)}`}
          value={attempt}
          autoFocus
          lang={mode === 'pinyin' ? 'en' : lang.speech}
          placeholder={mode === 'pinyin' ? 'go…' : lang.answerPlaceholder}
          onChange={(e) => setAttempt(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter' || e.nativeEvent.isComposing) return
            e.preventDefault()
            if (phase === 'live') submit()
            else advance()
          }}
          disabled={phase !== 'live'}
        />

        <div className="row" style={{ marginTop: '0.8rem' }}>
          {phase === 'live' ? (
            <button type="button" className="btn is-live" onClick={submit}>
              Lock it in
            </button>
          ) : (
            <button type="button" className="btn is-primary" onClick={advance}>
              {isLast ? 'See the result' : 'Next block'}
            </button>
          )}
        </div>
      </div>

      {phase === 'review' && result && (
        <GradeReport result={result} mode={mode} target={block} language={languageOf(island?.language).code} />
      )}
    </>
  )
}
